/**
 * The spell book, and the faith panel beside it.
 *
 * Windows of their own, like the kit: opened from their buttons whatever the
 * panels are set to, and put wherever somebody wants them. Each is a set of
 * tabs, one to a book or a kind of prayer, and a grid of pictures. Everything
 * about a spell is said in the tooltip over its picture: what it does, what it
 * asks for, and what it costs, with what is carried beside what is needed.
 */

import { useCallback, useMemo, useState } from 'react';
import type { PrayerWire, SpellWire } from '@inverse/protocol';
import { MODIFIER_NAMES } from '@inverse/protocol';
import { SPELL_ELEMENTS, SPELL_TIERS } from '@inverse/common';
import { CloseButton } from './CloseButton';
import { Glyph } from './Glyph';
import { useDraggableWindow } from './draggable';

type Hover = (held: { title: string; lines: string[] } | null) => void;

/** The picture on a book's tab, for the books there usually are. */
const BOOK_GLYPHS: Readonly<Record<string, string>> = {
  combat: 'burst',
  support: 'heart',
  teleports: 'portal',
  utility: 'coin',
  focus: 'eye',
  restoration: 'heart',
  protection: 'ward',
};

/** The tabs over a grid, and which is open. */
function useTabs(names: readonly string[], store: string): [string, (name: string) => void] {
  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem(store) ?? '';
    } catch {
      return '';
    }
  });
  const shown = names.includes(open) ? open : (names[0] ?? '');
  const choose = useCallback(
    (name: string) => {
      setOpen(name);
      try {
        window.localStorage.setItem(store, name);
      } catch {
        // Remembered for this visit only.
      }
    },
    [store],
  );
  return [shown, choose];
}

function Tabs({
  names,
  open,
  onOpen,
  onHover,
  tint,
}: {
  names: readonly string[];
  open: string;
  onOpen: (name: string) => void;
  onHover?: Hover;
  tint: string;
}): JSX.Element {
  return (
    <div className="book-tabs" role="tablist">
      {names.map((name) => (
        <button
          key={name}
          type="button"
          role="tab"
          aria-selected={name === open}
          aria-label={name}
          className={name === open ? 'book-tab on' : 'book-tab'}
          onClick={() => onOpen(name)}
          onPointerEnter={() => onHover?.({ title: name, lines: [] })}
          onPointerLeave={() => onHover?.(null)}
        >
          <Glyph icon={{ glyph: BOOK_GLYPHS[name.toLowerCase()] ?? 'book', colour: tint }} />
        </button>
      ))}
    </div>
  );
}

/** The ground a spell covers beyond the one thing it is aimed at. */
function covers(spell: SpellWire): string {
  const tiles = (many: number): string => `${many} ${many === 1 ? 'tile' : 'tiles'}`;

  if (spell.shape === 'line') {
    return spell.radius > 0
      ? `, catching everything between you and it and ${tiles(spell.radius)} past`
      : ', catching everything between you and it';
  }
  if (spell.radius <= 0) return '';

  switch (spell.shape) {
    case 'plus':
      return `, and everything in the four lines ${tiles(spell.radius)} out from it`;
    case 'diamond':
      return `, and everything within ${spell.radius} ${spell.radius === 1 ? 'step' : 'steps'} of it`;
    case 'circle':
      return `, and everything in a circle ${tiles(spell.radius * 2 + 1)} across`;
    default:
      return `, and everything within ${tiles(spell.radius)} of it`;
  }
}

/** What a spell is cast at, said the way somebody would say it. */
function aimedAt(spell: SpellWire): string {
  const reach = covers(spell);
  switch (spell.target) {
    case 'self':
      return 'Cast on yourself';
    case 'enemy':
      return `Cast at an enemy up to ${spell.range} tiles away${reach}`;
    case 'friend':
      return `Cast on a friend up to ${spell.range} tiles away${reach}`;
    case 'tile':
      return `Cast at a place up to ${spell.range} tiles away${reach}`;
    case 'item':
      return 'Cast on something in your bag';
  }
}

export function SpellsPanel({
  spells,
  magicLevel,
  mana,
  autocast,
  carried,
  itemName,
  onCast,
  onMenu,
  onAutocast,
  element,
  onHover,
  onClose,
}: {
  spells: readonly SpellWire[];
  magicLevel: number;
  mana: number;
  /** The spell set to cast from a staff, or nought. */
  autocast: number;
  carried: (itemId: number) => number;
  itemName: (itemId: number) => string;
  onCast: (spell: SpellWire) => void;
  onMenu: (spell: SpellWire, x: number, y: number) => void;
  /** Sets what a staff casts in a fight, from the row of tiers. */
  onAutocast: (spellId: number) => void;
  /** Which element the fighting panel is set to, since that is where it is chosen. */
  element: number;
  onHover?: Hover;
  onClose: () => void;
}): JSX.Element {
  const home = useCallback(
    (size: { width: number; height: number }) => ({
      x: window.innerWidth - size.width - 260,
      y: window.innerHeight - size.height - 70,
    }),
    [],
  );
  const { attach, style, onPointerDown } = useDraggableWindow('spells', { home });

  const books = useMemo(() => [...new Set(spells.map((spell) => spell.book))], [spells]);
  const [book, setBook] = useTabs(books, 'inverse.spells.book');

  /*
   * The elemental ladder is shown as a row of rungs rather than as its spells.
   *
   * Thirty-six squares of the same four icons is not a spellbook, it is a
   * wallpaper, and choosing between them is not a decision anybody makes: the
   * element is chosen against what is being fought, in the fighting panel, and
   * the rung is whatever somebody has trained up to. So the ladder is one row
   * of names at the top of the combat book, and the rest of the book is the
   * spells that are actually chosen one at a time.
   */
  const ladder = useMemo(() => spells.filter((spell) => spell.tier), [spells]);
  const rungOf = (tier: string): SpellWire | undefined =>
    ladder.find(
      (spell) =>
        spell.tier === tier && spell.damage?.element === (SPELL_ELEMENTS[element] ?? 'air'),
    );

  const shown = spells.filter((spell) => spell.book === book && !spell.tier);

  /*
   * Which rung is being cast, from the tier rather than from the spell.
   *
   * The spell changes when the element does, so anything that watched the spell
   * lost its place every time somebody swapped from fire to water. The tier is
   * what was chosen and the tier is what is shown.
   */
  const castingTier = ladder.find((spell) => spell.id === autocast)?.tier ?? '';
  const atRung = Math.max(
    0,
    SPELL_TIERS.findIndex((rung) => rung.name === castingTier),
  );
  const here = SPELL_TIERS[atRung];
  const lower = SPELL_TIERS[atRung - 1];
  const upper = SPELL_TIERS[atRung + 1];
  const below = lower ? rungOf(lower.name) : undefined;
  const above = upper ? rungOf(upper.name) : undefined;

  /** What a rung is, what it costs, and what it wants of you. */
  const describeRung = (rung: { name: string; level: number } | undefined) => {
    if (!rung) return null;
    const spell = rungOf(rung.name);
    const lines: string[] = [];

    if (spell?.description) lines.push(spell.description);
    lines.push(
      `Magic level ${rung.level}${magicLevel < rung.level ? ` (you are ${magicLevel})` : ''}`,
    );
    if (spell && spell.mana > 0) lines.push(`Mana: ${spell.mana} (you have ${Math.floor(mana)})`);
    for (const part of spell?.components ?? []) {
      lines.push(`${part.count}x ${itemName(part.item)} (you have ${carried(part.item)})`);
    }
    if (spell?.damage) {
      lines.push(
        `Hits for up to ${spell.damage.max}, and one more every ${spell.damage.perLevels} levels`,
      );
    }
    lines.push(
      'All four elements are learned together; the element is chosen in the fighting panel.',
    );

    return { title: spell ? spell.name : rung.name, lines };
  };

  const describe = (spell: SpellWire): { title: string; lines: string[] } => {
    const lines: string[] = [];
    if (spell.description) lines.push(spell.description);
    lines.push(
      `Magic level ${spell.level}${magicLevel < spell.level ? ' (you are ' + magicLevel + ')' : ''}`,
    );
    lines.push(aimedAt(spell));
    if (spell.mana > 0) lines.push(`Mana: ${spell.mana} (you have ${Math.floor(mana)})`);
    for (const part of spell.components) {
      lines.push(`${itemName(part.item)}: ${part.count} (you have ${carried(part.item)})`);
    }
    if (spell.damage) {
      const per =
        spell.damage.perLevels > 0 ? `, and one more every ${spell.damage.perLevels} levels` : '';
      const element = spell.damage.element !== 'none' ? ` with ${spell.damage.element}` : '';
      lines.push(`Hits for up to ${spell.damage.max}${element}${per}`);
    }
    if (spell.heals > 0) lines.push(`Heals ${spell.heals}`);
    for (const back of spell.restores) {
      lines.push(`Gives back ${back.amount} ${back.of === 'run' ? 'run energy' : back.of}`);
    }
    if (spell.status) {
      const what: string[] = [];
      if (spell.status.stun) what.push('unable to act');
      if (spell.status.bind) what.push('held where they stand');
      if (spell.status.poison > 0) {
        what.push(`taking ${spell.status.poison} every ${spell.status.every} ticks`);
      }
      for (const change of spell.status.modifiers) {
        what.push(
          `${change.stat} ${change.percent > 0 ? 'up' : 'down'} ${Math.abs(change.percent)}%`,
        );
      }
      lines.push(
        `Leaves ${spell.status.name.toLowerCase()} for ${spell.status.ticks} ticks${
          what.length > 0 ? `: ${what.join(', ')}` : ''
        }`,
      );
    }
    if (spell.requires.length > 0) lines.push('Asks for something more before it can be cast.');
    if (spell.autocast) {
      lines.push(
        autocast === spell.id
          ? 'Your staff casts this in a fight.'
          : 'Can be cast from a staff in a fight: right click to set it.',
      );
    }
    return { title: spell.name, lines };
  };

  return (
    <div className="book-window" ref={attach} style={style}>
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Spellbook</span>
        <span className="panel-count">{Math.floor(mana)} mana</span>
        <CloseButton onClose={onClose} />
      </div>

      <Tabs names={books} open={book} onOpen={setBook} onHover={onHover} tint="#8c96ff" />

      {book === 'Combat' && ladder.length > 0 ? (
        /*
         * The elemental ladder: where somebody is on it, and the way up and down.
         *
         * A row of nine buttons said the same thing nine times and left the one
         * that mattered hard to find. This is one readout of what a staff is
         * casting, with a step either way: the rung is what somebody is on, and
         * the step up is closed until the level for it is reached, with the
         * level itself written where the arrow was so the answer to "why not"
         * is on the button that refused.
         */
        <div className="ladder">
          <button
            type="button"
            className="rung-step"
            aria-label="The rung below"
            disabled={!below}
            onClick={() => below && onAutocast(below.id)}
            onPointerEnter={() => below && onHover?.(describeRung(lower))}
            onPointerLeave={() => onHover?.(null)}
          >
            {'\u2039'}
          </button>

          <span
            className="rung-now"
            onPointerEnter={() => onHover?.(describeRung(here))}
            onPointerLeave={() => onHover?.(null)}
          >
            <span className="rung-name">{here?.name ?? 'None'}</span>
            <span className="rung-level">{here ? `Level ${here.level}` : ''}</span>
          </span>

          <button
            type="button"
            className="rung-step"
            aria-label="The rung above"
            disabled={!above || magicLevel < (upper?.level ?? 0)}
            onClick={() => above && onAutocast(above.id)}
            onPointerEnter={() => upper && onHover?.(describeRung(upper))}
            onPointerLeave={() => onHover?.(null)}
          >
            {upper && magicLevel < upper.level ? (
              <span className="rung-wants">{upper.level}</span>
            ) : (
              '\u203a'
            )}
          </button>
        </div>
      ) : null}

      <div className="book-grid">
        {shown.map((spell) => {
          const locked = magicLevel < spell.level;
          const short =
            !locked &&
            (mana < spell.mana || spell.components.some((part) => carried(part.item) < part.count));
          return (
            <button
              key={spell.id}
              type="button"
              className={[
                'book-cell',
                locked ? 'locked' : '',
                short ? 'short' : '',
                autocast === spell.id ? 'on' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-label={spell.name}
              data-name={spell.name}
              onClick={() => !locked && onCast(spell)}
              onContextMenu={(event) => {
                event.preventDefault();
                onMenu(spell, event.clientX, event.clientY);
              }}
              onPointerEnter={() => onHover?.(describe(spell))}
              onPointerLeave={() => onHover?.(null)}
            >
              <Glyph icon={spell.icon} />
            </button>
          );
        })}
        {shown.length === 0 ? <p className="hint">No spells are written yet.</p> : null}
      </div>
    </div>
  );
}

export function FaithPanel({
  prayers,
  faithLevel,
  faith,
  maxFaith,
  held,
  onToggle,
  onHover,
  onClose,
}: {
  prayers: readonly PrayerWire[];
  faithLevel: number;
  faith: number;
  maxFaith: number;
  /** The prayers held up. */
  held: ReadonlySet<number>;
  onToggle: (prayer: PrayerWire, on: boolean) => void;
  onHover?: Hover;
  onClose: () => void;
}): JSX.Element {
  const home = useCallback(
    (size: { width: number; height: number }) => ({
      x: window.innerWidth - size.width - 480,
      y: window.innerHeight - size.height - 70,
    }),
    [],
  );
  const { attach, style, onPointerDown } = useDraggableWindow('faith', { home });

  const kinds = useMemo(() => [...new Set(prayers.map((prayer) => prayer.category))], [prayers]);
  const [kind, setKind] = useTabs(kinds, 'inverse.faith.kind');
  const shown = prayers.filter((prayer) => prayer.category === kind);

  const describe = (prayer: PrayerWire): { title: string; lines: string[] } => {
    const lines: string[] = [];
    if (prayer.description) lines.push(prayer.description);
    lines.push(
      `Faith level ${prayer.level}${faithLevel < prayer.level ? ' (you are ' + faithLevel + ')' : ''}`,
    );
    for (const one of prayer.modifiers) {
      lines.push(
        `${MODIFIER_NAMES[one.stat] ?? one.stat} ${one.percent > 0 ? '+' : ''}${one.percent}%`,
      );
    }
    if (prayer.drain > 0) lines.push(`Uses ${prayer.drain} faith a minute`);
    if (prayer.group) {
      const others = prayers.filter((one) => one.group === prayer.group && one.id !== prayer.id);
      if (others.length > 0) lines.push(`Puts down ${others.map((one) => one.name).join(', ')}`);
    }
    if (prayer.requires.length > 0) lines.push('Asks for something more before it can be used.');
    return { title: prayer.name, lines };
  };

  return (
    <div className="book-window faith-window" ref={attach} style={style}>
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Faith</span>
        <span className="panel-count">
          {Math.ceil(faith)} / {maxFaith}
        </span>
        <CloseButton onClose={onClose} />
      </div>

      <Tabs names={kinds} open={kind} onOpen={setKind} onHover={onHover} tint="#ddb43f" />

      <div className="book-grid">
        {shown.map((prayer) => {
          const locked = faithLevel < prayer.level;
          const on = held.has(prayer.id);
          return (
            <button
              key={prayer.id}
              type="button"
              className={['book-cell', locked ? 'locked' : '', on ? 'on' : '']
                .filter(Boolean)
                .join(' ')}
              aria-label={prayer.name}
              aria-pressed={on}
              data-name={prayer.name}
              onClick={() => !locked && onToggle(prayer, !on)}
              onPointerEnter={() => onHover?.(describe(prayer))}
              onPointerLeave={() => onHover?.(null)}
            >
              <Glyph icon={prayer.icon} />
            </button>
          );
        })}
        {shown.length === 0 ? <p className="hint">No prayers are written yet.</p> : null}
      </div>
    </div>
  );
}
